import { Component, OnInit, Optional, OnDestroy } from "@angular/core";
import { ActivatedRoute, Params } from "@angular/router";

import { Observable ,  BehaviorSubject, Subscription, of, from, combineLatest, forkJoin } from "rxjs";
import { mergeMap, tap, map, finalize, delay } from "rxjs/operators";


import { BaseControlComponent } from "../../../form-controls/components/base-control/base-control.component";
import { AuthorizationRequest, PaymentService, PaymentReceipt, calculateTotal } from "../../services/payment.service";
import { LoaderService } from "../../../common/services/loader.service";
import { FlowInstanceService } from "../../../flow/services/flow-instance.service";
import { ApplicationPayment, Payment, PaymentStatus, PaymentSearchCriteria, PaymentInstrument, writeToken, AuthorizationResponse } from "../../models/paymentItem.model";
import { isJSON } from "../../../Helpers/UtilityFunctions";
import { ContextInteractionService } from "../../../form-controls/services/context-interaction.service";
import { FlowButtonOverrideService } from "../../../flow/services/flow-button-override.service";
import { PaymentReceivedDateRequest } from "../../../flow/models/flow.model";
import { Button } from "../../../flow/models/flow.model";

@Component({
    selector: "payment-respose",
    templateUrl: "./payment-response.component.html",
    styleUrls: ["./payment-response.component.scss"],
    providers: [FlowButtonOverrideService],
    standalone: false
})
export class PaymentResponseComponent extends BaseControlComponent implements OnInit, OnDestroy {
    static componentName = "PaymentResponseComponent";
    authorizationRequest: AuthorizationRequest;
    authorizationResponse: AuthorizationResponse;
    awaitingPaymentStatuses = [PaymentStatus.Pending, PaymentStatus.Partial, PaymentStatus.Overdue];
    cancellationText = "";
    creditedTypes = ["Credit", "Debit", "Check"];
    dataLoading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
    isCredited: boolean = false;
    isLiquidAsset: boolean = false;
    isError: boolean = false;
    liquidTypes = ["LocalCheck"];
    loadingInvoice$ = new BehaviorSubject<boolean>(true);
    obj: Object;
    paymentId: string;
    receiptObj: PaymentReceipt;
    records: ApplicationPayment[];
    workFlowId: string;
    subscription: Subscription = new Subscription();

    get config(): any {
        return this.componentData.config;
    }

    set config(value: any) {
        this.componentData.config = value;
    }

    get total() {
        if (!this.authorizationRequest || !this.authorizationRequest.paymentInformation) return "";
        return calculateTotal(this.authorizationRequest.paymentInformation.paymentItems);
    }

    constructor(
        public readonly activatedRoute: ActivatedRoute,
        public readonly paymentService: PaymentService,
        public readonly loader: LoaderService,
        public readonly flowService: FlowInstanceService,
        public readonly contextService: ContextInteractionService,
        @Optional() public readonly buttonOverride: FlowButtonOverrideService

    ) {
        super(contextService);
    }


    ngOnInit() {
        super.ngOnInit()
        if (!this.componentData.contextTarget) {
            this.componentData.contextTarget = "$.data.paymentResponse";
        }
		this.authorizationRequest = this.getOriginalRequest();
		if (this.buttonOverride) {
			this.buttonOverride.flow = this.flowComponent;
			this.buttonOverride.incrementFunction = this._incrementFunction.bind(this);
			this.buttonOverride.decrementFunction = this._decrementFunction.bind(this);
			this.buttonOverride.setValidatorNextButton((btn: Button) => {
				return btn.isNext && (this.authorizationRequest != null && this.dataLoading.value == false && (this.records == null || this.isError || this.dataLoading.value));
			});
		}        
		if (this.authorizationRequest != null && this.authorizationRequest.accountInformation != null) {
			this.isLiquidAsset = this.liquidTypes.indexOf(this.authorizationRequest.accountInformation.paymentType) !== -1;
			this.isCredited = this.creditedTypes.indexOf(this.authorizationRequest.accountInformation.paymentType) !== -1;
			const existingCriteria = new PaymentSearchCriteria("", this.authorizationRequest.applicationNumber, []);
			const search$ = this.paymentService.search(existingCriteria).pipe(
				mergeMap((results: ApplicationPayment[]) => {
					for (let p = 0; p < results.length; p++) {
						if (results[p].invoiceDocumentType == null || results[p].invoiceDocumentType != this.componentData.config.invoiceType.name) results[p].invoiceDocumentType = this.componentData.config.invoiceType.name;
						if (results[p].receiptDocumentType == null || results[p].receiptDocumentType != this.componentData.config.receiptType.name) results[p].receiptDocumentType = this.componentData.config.receiptType.name;
					}
					this.records = results;
					if (this.isCredited) {
						const authResponse = this.getContextValue();
						if (authResponse) {
							this.authorizationResponse = JSON.parse(authResponse);
						}
						let obs$ = of(null);
						if (this.isEnabled || this.enableEditing) {
							obs$ = this.activatedRoute.queryParams.pipe(map((params) => { return params["id"]; }));
						}
						return obs$.pipe(
							mergeMap((id: string) => {
								if (id != null) {
									return from(this.paymentService.detokenizeResponse(id)).pipe(
										map((response: any) => {
											this.saveResponseInContext(response);
											this.authorizationResponse = JSON.parse(response as any) as AuthorizationResponse;
											return this.authorizationResponse;
										})
									)
								} else {
									return of(this.authorizationResponse);
								}
							}),
							mergeMap((response: AuthorizationResponse) => {
								this.buildReceiptObj();
								this.isError = this.authorizationResponse.Error == "True";
								this.cancellationText = `PaymentStatus: ${this.authorizationResponse.ErrorMessage}`;
								this.paymentId = this.receiptObj.authorizationResponse.Token;
								if (this.isError || (!this.isEnabled && !this.enableEditing)) return of(null).pipe(finalize(() => { this.dataLoading.next(false); }));
								return this.updatePaymentRecords();
							})
						);
					} else if (this.isLiquidAsset) {
						this.dataLoading.next(false);
						this.loadingInvoice$.next(false);
						this.buildReceiptObj();
						return of(null);
					}
				}
				)
			).subscribe(
				(data: any) => { this.loader.toggleLoader(false); },
				(error) => {
					this.loader.toggleLoader(false);
					this.isError = true;
					this.cancellationText = "Payment failed to process.";
					console.error(error);
				}
			);

			this.subscription.add(search$);
		}
		else {
			this.dataLoading.next(false);
		}
        
    }

    ngOnDestroy() {
        if (this.subscription != null) this.subscription.unsubscribe();
        if (this.buttonOverride != null) this.buttonOverride.resetOverride();
    }

    protected _incrementFunction(button: Button): void {
        this.buttonOverride.callOriginalFunction(button);
    }

    protected _decrementFunction(button: Button): void {
        this.buttonOverride.callOriginalFunction(button);
    }

    buildReceiptObj() {
        this.receiptObj = new PaymentReceipt();
        this.receiptObj.ApplicationNumber = this.authorizationRequest.applicationNumber;
        this.receiptObj.authorizationResponse = this.authorizationResponse; 
    }

	getOriginalRequest(): AuthorizationRequest {
        let location = this.config.payments;
        if (!location) location = "$.data.paymentCheckout";
        const data = this.getContextData(`${location}.payments`);
        if (data) {
            if (isJSON(data)) return JSON.parse(data) as AuthorizationRequest;
            if (data instanceof AuthorizationRequest) return data as AuthorizationRequest;
        }
        return data;
    }

    getResponseProperty(property: string): string {
        let data = this.authorizationResponse[property];
        if (data === undefined || data === null) return "";
        data = data.replace("+", " ");
        return data;
    }

    onReceiptIssued(payments: ApplicationPayment[]): void {
        let recvdApps = payments.filter(p => p.paymentReceivedDate != null && (new Date(p.paymentReceivedDate)).toLocaleDateString().length > 0);
        if (recvdApps.length > 0) {
            let delay$: Observable<any> = of(recvdApps).pipe(
                delay(1000),
                map((pmts: ApplicationPayment[]) => {
                    let reqs: PaymentReceivedDateRequest[] = [];
                    pmts.forEach(p => {
                        let existing = reqs.find(r => r.paymentReceivedDate == p.paymentReceivedDate);
                        if (existing != null) {
                            existing.applicationNumbers.push(p.applicationNumber);
                        } else {
                            let req: PaymentReceivedDateRequest = {
                                applicationNumbers: [p.applicationNumber],
                                paymentReceivedDate: p.paymentReceivedDate,
                                isElectronicPayment: p.paymentRecords.length > 0 && p.paymentRecords.find(r => this.creditedTypes.includes(r.paymentType)) != null
                            } as PaymentReceivedDateRequest;
                            reqs.push(req);
                        }

                    })
                    return reqs;
                }),
                mergeMap((reqs: PaymentReceivedDateRequest[]) => {
                    this.loader.toggleLoader(true);
                    let obs$: Observable<any>[] = [];
                    reqs.forEach(r => {
                        obs$.push(this.flowService.setPaymentReceivedDates(r));
                    });
                    if (obs$.length > 0) {
                        return forkJoin(obs$);
                    } else {
                        return of(null);
                    }
                })
            );
            let subs$: Subscription = delay$.subscribe(
                (req: any) => {
                    if (req != null) {
                        this.saveContextData("$.paymentReceivedDate.value", (new Date()).toLocaleDateString());
                    }
                    this.loader.toggleLoader(false);
                },
                (error) => {
                    console.error(error);
                    this.loader.toggleLoader(false);
                }
            );
            this.subscription.add(subs$);
        } else {
            this.loader.toggleLoader(false);
        }
    }

    saveResponseInContext(response: any): void {
        if (this.enableEditing) {
            this.setContextValue(response);
            this.flowComponent.promiseSaveContext().then(() => {
                console.log("context saved");
            })
        }
    }

    updatePaymentRecords(): Observable<any> {
        //save the appropriate doc type to the payment record, so that payment processing will know what type to use when it generates a document
        const amountAuthorized = Number(this.authorizationResponse.Amount);
        const paymentObservables: Observable<any>[] = [];
        const authorization = {
            id: this.authorizationResponse.Token,
            instrumentType: this.authorizationResponse.PaymentType,
            instrumentAmount: amountAuthorized,
            appliedAmount: 0,
            token: this.authorizationResponse.Token
        } as PaymentInstrument;
        if (this.authorizationRequest.paymentInformation.paymentItems == null || this.authorizationRequest.paymentInformation.paymentItems.length === 0) {
            paymentObservables.push(of(null));
        }
        for (let p = 0; p < this.authorizationRequest.paymentInformation.paymentItems.length; p++) {
            let availableFunds = authorization.instrumentAmount - authorization.appliedAmount;
            const receivable = this.authorizationRequest.paymentInformation.paymentItems[p];
            const record: ApplicationPayment = this.records.find(r => r.id === receivable.id);
            if (record.amountDue > 0) {
                if (this.awaitingPaymentStatuses.indexOf(record.paymentStatus) !== -1) {
                    const amountPaid = Number(record.amountPaid);
                    const remainingDue = receivable.fee - amountPaid;

                    if (availableFunds >= remainingDue) {
                        record.amountPaid = remainingDue;
                        record.paymentStatus = PaymentStatus.Paid;
                        authorization.appliedAmount = authorization.appliedAmount + remainingDue;
                    } else if (availableFunds > 0) {
                        record.amountPaid = amountPaid + availableFunds;
                        record.paymentStatus = PaymentStatus.Partial;
                        authorization.appliedAmount = authorization.instrumentAmount;
                    }
                }
                if (this.isCredited && record.amountDue > 0) {
                    let payment = {
                        applicationPaymentId: receivable.id,
                        amount: record.amountPaid,
                        paymentType: this.authorizationRequest.accountInformation.paymentType,
                        tokenId: (this.authorizationResponse) ? this.authorizationResponse.Token : "",
                        instrumentAmount: record.amountPaid,
                        paymentReceivedDate: new Date()
                    } as Payment;
                    record.paymentRecords.push(payment);
                }
                record.department = this.authorizationRequest.paymentInformation.paymentItems[p].department;
                record.reporting = this.authorizationRequest.paymentInformation.paymentItems[p].reporting;
                record.ali = this.authorizationRequest.paymentInformation.paymentItems[p].ali;
                record.fund = this.authorizationRequest.paymentInformation.paymentItems[p].fund;
                record.program = this.authorizationRequest.paymentInformation.paymentItems[p].program;
                record.revenueSourceCode = this.authorizationRequest.paymentInformation.paymentItems[p].revenueSourceCode;
                record.accountDescription = this.authorizationRequest.paymentInformation.paymentItems[p].accountDescription;

                record.invoiceDocumentType = this.componentData.config.invoiceType.name;
                record.receiptDocumentType = this.componentData.config.receiptType.name;

                const observable = this.paymentService.savePaymentRecord(record).pipe(
                    tap((savedRecord: ApplicationPayment) => {
                        let index = this.records.findIndex(r => r.id == savedRecord.id);
                        if (index != -1) {
                            this.records.splice(index, 1, savedRecord); //replace the existing record in the collection with the saved one, in order to track the associated payments for next go round, in case it was a partial payment
                        }
                    })                    
                )
                paymentObservables.push(observable);
            }
            else paymentObservables.push(of(null));
        }
        return combineLatest(paymentObservables).pipe(finalize(() => { this.dataLoading.next(false); }));
    }
}